'use client';

import { useLocale, useTranslations } from '@dealerportal/i18n/client';
import { FormatDate, getDateFormat } from '@dealerportal/utilities';
import { type FC, useState } from 'react';

interface Props {
  id: string;
  name: string;
  className: string;
  dates: string;
  isNFD: boolean;
  minDate: Date;
}

type SupportedLocale = 'en' | 'pt' | 'es';

/**
 * Reads the `yyyy-MM-dd` value of a date input as a local-time date. `new Date(value)`
 * would parse it as UTC and shift the day backwards west of Greenwich.
 */
function parseInputValue(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
}

/**
 * Appointment date field for Act-On forms. The visible input is a native date
 * input; the submitted value stays in the locale format the form expects, so it
 * travels in a hidden input.
 */
export const DatePickerInput: FC<Props> = (props) => {
  const t = useTranslations('ui');
  const localeCode = (useLocale() || 'en') as SupportedLocale;
  const [value, setValue] = useState('');
  const [showLabel, setShowLabel] = useState(true);

  // Keys are `Date.getDay()` values the dealer takes no appointments on.
  const closedDays = Object.keys(JSON.parse(props?.dates ?? '{}'));
  const selected = parseInputValue(value);
  const isClosedDay =
    selected !== null && closedDays.includes(selected.getDay().toString());

  return (
    <>
      <input
        // A date input ignores `placeholder`, so the field stays text until used.
        type={showLabel && !value ? 'text' : 'date'}
        id={props.id}
        className={props.className}
        placeholder={t('preferredDate')}
        aria-label={t('preferredDate')}
        min={FormatDate(props?.minDate)}
        value={value}
        readOnly={props?.isNFD}
        onFocus={(event) => {
          setShowLabel(false);
          const input = event.currentTarget;
          if (input.type === 'date') {
            input.showPicker?.();
          }
        }}
        onBlur={() => setShowLabel(true)}
        onChange={(event) => {
          const input = event.currentTarget;
          const picked = parseInputValue(input.value);
          const closed =
            picked !== null && closedDays.includes(picked.getDay().toString());
          input.setCustomValidity(closed ? t('unavailableDate') : '');
          setValue(input.value);
        }}
      />
      <input
        type="hidden"
        name={props.name}
        value={
          selected && !isClosedDay
            ? FormatDate(selected, getDateFormat(localeCode), localeCode)
            : ''
        }
      />
      {isClosedDay && (
        <p className="ui-text-sm ui-text-red-600">{t('unavailableDate')}</p>
      )}
    </>
  );
};

export default DatePickerInput;
